import { Rectangle } from '../layout/position/rectangle';
import { HorizontalPosition } from '../layout/position/horizontalPosition';
import { VerticalPosition } from '../layout/position/verticalPosition';


/**
 * Given a DOM element, returns the element's x/y relative to the root viewport and the element's width/height. If the
 * element is undefined, returns all 0s. This works by calling `getBoundingClientRect()`, if you're interested.
 */
export const getRect = (element: HTMLElement | null | undefined): Rectangle => {
  if (!element) {
    return {
      x:      0,
      y:      0,
      width:  0,
      height: 0,
    };
  }

  const clientRect = element.getBoundingClientRect();
  return {
    x:      clientRect.left,
    y:      clientRect.top,
    width:  clientRect.width,
    height: clientRect.height,
  };
};


/**
 * Gives you a `left` css style to position an element relative to the given rect.
 * @param alignment - The position you want relative to the given rect
 * @param rect - The rect to position another element around.
 */
export const xOffsetFor = (alignment: HorizontalPosition, rect: Rectangle): string => {
  switch (alignment) {
    case 'left':
      return 'left: '+rect.x+'px';

    case 'center':
      return 'left: '+(rect.x + rect.width / 2) +'px';

    case 'right':
      return 'left: '+(rect.x + rect.width)+'px';
  }
};


/**
 * Gives you a `top` css style to position an element relative to the given rect.
 * @param alignment - The position you want relative to the given rect
 * @param rect - The rect to position another element around.
 */
export const yOffsetFor = (alignment: VerticalPosition, rect: Rectangle): string => {
  switch (alignment) {
    case 'above':
      return 'top: '+rect.y+'px';

    case 'center':
      return 'top: '+(rect.y + rect.height / 2)+'px';

    case 'below':
      return 'top: '+(rect.y + rect.height)+'px';
  }
};


/**
 * For any position, horizontal or vertical, gives you the percent by which to translate an element to place it relative
 * to some other element (doesn't matter the details of the specific elements)
 */
export const translatePercentFor = (alignment: HorizontalPosition | VerticalPosition): string => {
  switch (alignment) {
    case 'left':
    case 'above':
      return '-100%';

    case 'center':
      return '-50%';

    case 'right':
    case 'below':
      return '0%';
  }
};
